import { ListingItemtype, ListingSuggestionType } from '@utils/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDebouncedCallback } from 'use-debounce'
import { useTrans } from '../../../hooks'
import useRoveFocus from '../../../hooks/useRoveFocus'
import { Search } from '../../icons'
import styles from './SearchBarListing.module.sass'
import { WLink } from '@components/atoms'

interface Props {
  suggestions: ListingSuggestionType[]
  placeholder?: string
  open?: boolean
  className?: string
  onClick?: (suggestion: ListingItemtype) => void
  setNoResults?: (value: boolean) => void
  searchValue: string
  setSearchValue: (value: any) => void
  isFetching?: boolean
}

const SearchBarListing = (props: Props) => {
  const {
    suggestions = [],
    placeholder = '',
    open = false,
    className = '',
    onClick = undefined,
    setNoResults = undefined,
    searchValue = '',
    setSearchValue = undefined,
    isFetching = false,
  } = props

  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const t = useTrans()

  const ref = useRef(null)
  const searchBarInputRef = useRef(null)
  const searchBarDropDown = useRef(null)
  const searchSuggestionsContainerRef = useRef(null)
  const [value, setValue] = useState(searchValue)
  const [currentFocus, setCurrentFocus] = useRoveFocus({
    element: searchBarInputRef.current,
    items: searchSuggestionsContainerRef?.current?.children || [],
    onClick: () => {
      setIsOpen(false)
    },
  })

  useEffect(() => {
    setValue(searchValue)
  }, [searchValue])

  useEffect(() => {
    if (value?.length >= 3 || value?.length === 0) handleDebouncedSearch(value)
  }, [value])

  const handleDebouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchValue(value)
  }, 500)

  useEffect(() => {
    setSearchValue(debouncedSearchValue)
  }, [debouncedSearchValue])

  useHotkeys(
    'esc',
    () => {
      setIsOpen(false)
    },
    { enableOnFormTags: ['INPUT'] }
  )

  useHotkeys(
    'enter',
    () => {
      // Blocca se:
      // 1. La ricerca è in corso (isFetching)
      // 2. Il testo nell'input è diverso da quello cercato (c'è una ricerca in attesa di debounce)
      if (isFetching || value !== searchValue) {
        return
      }
      setIsOpen(false)
      if (filteredSuggestions.length === 0) {
        setNoResults?.(true)
      } else {
        //console.log('search submitted with: ', debouncedSearchValue)
      }
    },
    { enableOnFormTags: ['INPUT'] }
  )

  const filteredSuggestions = useMemo(() => {
    // setSearchValue(debouncedSearchValue)   rimosso per evitare loop infiniti
    if (!debouncedSearchValue || debouncedSearchValue.length < 3) return []
    return suggestions
  }, [debouncedSearchValue, suggestions]) //mockElementSearch])

  const highlightMatch = (text: string) => {
    const index = text.toLowerCase().indexOf(debouncedSearchValue.toLowerCase())
    if (index === -1) return text

    return (
      <>
        {text.slice(0, index)}
        <span className={styles.highlight}>
          {text.slice(index, index + debouncedSearchValue.length)}
        </span>
        {text.slice(index + debouncedSearchValue.length)}
      </>
    )
  }

  useEffect(() => {
    debouncedSearchValue.length > 2 ? setIsOpen(true) : setIsOpen(false)
  }, [debouncedSearchValue])

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!!ref?.current && !ref?.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document?.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  return (
    <div
      className={`${styles.root} ${open ? styles['root--mobileOpen'] : ''} ${className}`}
      ref={ref}
    >
      <div className={styles.icon}>
        <Search />
      </div>
      <input
        type="text"
        name="seachBar"
        ref={searchBarInputRef}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        className={styles.input}
      />
      {isOpen && (
        <div className={styles.dropDownContainer} ref={searchBarDropDown}>
          <p className={styles.dropDownTitle}>
            {t(
              `${
                filteredSuggestions.length > 0
                  ? 'Risultati ricerca'
                  : isFetching
                  ? 'Caricamento...'
                  : 'Nessun risultato trovato'
              }`
            )}
          </p>
          <div className={styles.searchSuggestionsContainer} ref={searchSuggestionsContainerRef}>
            {filteredSuggestions.map((suggestion, index) => (
              <WLink
                className={`${styles.searchSuggestion} ${
                  currentFocus === index ? styles['searchSuggestion--focused'] : ''
                }`}
                href={suggestion.url}
                key={index}
              >
                <span className={styles.searchSuggestionText}>
                  {highlightMatch(suggestion.text)}
                </span>
                <span className={styles.searchSuggestionType}>{t(suggestion.modelName)}</span>
              </WLink>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchBarListing
